<template>
  <!-- Tabla de proyectos de la cartera: una fila por proyecto, clicable para abrir su Dashboard PRM. -->
  <summary-card :title="$t('prm_portfolio_projects_title')" :subtitle="subtitle" :mode="mode">
    <div class="ptable-wrap">
      <table class="ptable">
        <thead>
          <!-- Cabecera de grupos (Identificación · Avance · Margen · Económico · Estado). -->
          <tr class="grp">
            <th colspan="3">{{ $t('prm_portfolio_grp_identification') }}</th>
            <th colspan="3" class="gsep">{{ $t('prm_portfolio_grp_progress') }}</th>
            <th colspan="4" class="gsep">{{ $t('prm_portfolio_grp_margin') }}</th>
            <th colspan="4" class="gsep">{{ $t('prm_portfolio_grp_economic') }}</th>
            <th colspan="1" class="gsep">{{ $t('prm_portfolio_grp_state') }}</th>
          </tr>
          <tr class="cols">
            <th v-for="c in columns" :key="c.key" :class="[c.align, { gsep: c.groupStart }]">
              <span>{{ $t(c.label) }}</span>
              <source-chip v-if="mode" :source="c.source" :tip-override="c.tip ? $t(c.tip) : ''"></source-chip>
            </th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="r in rows" :key="r.id" class="prow" :class="{ current: r.id === currentProjectId }"
            role="button" tabindex="0" @click="$emit('open', r)" @keydown.enter="$emit('open', r)"
            @keydown.space.prevent="$emit('open', r)">
            <!-- Identificación -->
            <td class="left proj">
              <group-tag :group="r.group" :color="r.groupColor"></group-tag>
              <span class="p-code" :style="{ color: r.groupColor }">{{ r.code || '-' }}</span>
              <span class="p-name" v-tip="r.name">{{ r.name }}</span>
              <span v-if="r.id === currentProjectId" class="p-actual">{{ $t('prm_portfolio_current') }}</span>
            </td>
            <td class="left ptrunc" v-tip="r.client">{{ r.client || '-' }}</td>
            <td class="left ptrunc">{{ r.pm || '-' }}</td>
            <!-- Avance -->
            <td class="right gsep">{{ pct1(r.actualProgress) }}</td>
            <td class="right">{{ pct1(r.plannedProgress) }}</td>
            <td class="center"><sem-dot v-if="sp(r)" :state="sp(r)" :title="$t('prm_dash_rag_' + sp(r))"></sem-dot><span v-else class="dash">-</span></td>
            <!-- Margen -->
            <td class="right gsep">{{ r.hasPrm ? pct(r.commercialMargin) : '-' }}</td>
            <td class="right">{{ r.hasPrm ? pct(r.actualMargin) : '-' }}</td>
            <td class="right" :style="{ color: mdevColor(r) }">{{ money(dev(r), true) }}</td>
            <td class="center"><sem-dot v-if="sm(r)" :state="sm(r)" :title="$t('prm_dash_rag_' + sm(r))"></sem-dot><span v-else class="dash">-</span></td>
            <!-- Económico -->
            <td class="right gsep">{{ money(r.changeOrders) }}</td>
            <td class="right">{{ money(r.totalAmount) }}</td>
            <td class="right">{{ money(bl(r)) }}</td>
            <td class="right">{{ r.hasPrm ? pct(r.poc) : '-' }}</td>
            <!-- Estado -->
            <td class="center gsep">
              <sem-dot :state="st(r) || 'none'" :title="st(r) ? $t('prm_dash_rag_' + st(r)) : $t('prm_portfolio_no_data')"></sem-dot>
            </td>
          </tr>
          <tr v-if="!rows.length">
            <td :colspan="columns.length" class="empty">{{ $t('prm_portfolio_no_projects') }}</td>
          </tr>
        </tbody>
      </table>
    </div>
  </summary-card>
</template>

<script>
  import SummaryCard from '../summary/summary-card.vue'
  import SourceChip from '../summary/source-chip.vue'
  import SemDot from '../dashboard/sem-dot.vue'
  import GroupTag from './group-tag.vue'
  import { tip } from '../shared/tip'
  import { fmtEur, fmtPct, fmtEurSigned } from '../shared/format'
  import { ACCENT } from '../dashboard/dashboard-model'
  import { semProgress, semMargin, semState, backlog, marginDev } from './portfolio-model'

  // Definición de columnas (para la cabecera con chips de fuente). El cuerpo se pinta explícito
  // porque cada celda tiene su formato (chip de grupo, punto de semáforo, importe, porcentaje).
  const COLUMNS = [
    { key: 'project', label: 'prm_portfolio_col_project', align: 'left', source: 'admin' },
    { key: 'client', label: 'prm_portfolio_col_client', align: 'left', source: 'resumen' },
    { key: 'pm', label: 'prm_portfolio_col_pm', align: 'left', source: 'prm' },
    { key: 'actualProgress', label: 'prm_portfolio_col_progress_real', align: 'right', source: 'medic', groupStart: true },
    { key: 'plannedProgress', label: 'prm_portfolio_col_progress_plan', align: 'right', source: 'planif' },
    { key: 'semProgress', label: 'prm_portfolio_col_sem_progress', align: 'center', source: 'calc', tip: 'prm_portfolio_tip_sem_progress' },
    { key: 'marginCom', label: 'prm_portfolio_col_commercial_margin', align: 'right', source: 'prm', groupStart: true },
    { key: 'marginProj', label: 'prm_portfolio_col_project_margin', align: 'right', source: 'prm' },
    { key: 'marginDev', label: 'prm_portfolio_col_margin_dev', align: 'right', source: 'calc', tip: 'prm_portfolio_tip_margin_dev' },
    { key: 'semMargin', label: 'prm_portfolio_col_sem_margin', align: 'center', source: 'calc', tip: 'prm_portfolio_tip_sem_margin' },
    { key: 'changeOrders', label: 'prm_portfolio_col_change_orders', align: 'right', source: 'prm', groupStart: true },
    { key: 'totalSale', label: 'prm_portfolio_col_total_sale', align: 'right', source: 'prm' },
    { key: 'backlog', label: 'prm_portfolio_col_backlog', align: 'right', source: 'calc', tip: 'prm_portfolio_tip_backlog' },
    { key: 'poc', label: 'prm_portfolio_col_poc', align: 'right', source: 'calc', tip: 'prm_portfolio_tip_poc' },
    { key: 'status', label: 'prm_portfolio_col_state', align: 'center', source: 'calc', groupStart: true, tip: 'prm_portfolio_tip_state' },
  ]

  export default {
    name: 'PrmPortfolioProjectsTable',

    components: { SummaryCard, SourceChip, SemDot, GroupTag },

    directives: { tip },

    props: {
      rows: { type: Array, default: () => [] },   // filas normalizadas (portfolio-model.normalize)
      currentProjectId: { type: Number, default: null },
      subtitle: { type: String, default: '' },
      mode: { type: Boolean, default: false },
    },

    data: () => ({ columns: COLUMNS }),

    methods: {
      sp (r) { return semProgress(r) },
      sm (r) { return semMargin(r) },
      st (r) { return semState(r) },
      bl (r) { return backlog(r) },
      dev (r) { return marginDev(r) },
      pct (v) { return fmtPct(v) },
      pct1 (v) { return v == null ? '-' : `${Number(v).toFixed(1)} %` },
      money (v, signed = false) { return v == null ? '-' : (signed ? fmtEurSigned(v) : fmtEur(v)) },
      mdevColor (r) {
        const d = marginDev(r)
        return d == null ? '#1e3046' : (d < 0 ? ACCENT.bad : ACCENT.good)
      },
    },
  }
</script>

<style lang="scss" scoped>
  .ptable-wrap { overflow-x: auto; }
  .ptable { width: 100%; min-width: 1060px; border-collapse: collapse; }

  .ptable thead th {
    background: #3d6b9a; color: #fff; font-size: 9px; font-weight: 700; text-transform: uppercase;
    letter-spacing: .03em; padding: 4px 6px; vertical-align: middle;
  }
  // Cabeceras de columna en 2 líneas si hace falta (columnas estrechas), para no estirar la tabla.
  .ptable thead tr.cols th { white-space: normal; line-height: 1.15; }
  .ptable thead tr.grp th { background: #34608c; border-bottom: 1px solid #4d76a1; text-align: center; white-space: nowrap; }
  .ptable thead tr.cols th.left { text-align: left; }
  .ptable thead tr.cols th.right { text-align: right; }
  .ptable thead tr.cols th.center { text-align: center; }
  // Separador sutil entre grupos de columnas.
  .gsep { box-shadow: inset 1px 0 0 0 #d9e0e9; }
  .ptable thead .gsep { box-shadow: inset 1px 0 0 0 #5d87ad; }

  .ptable tbody td {
    border-bottom: 1px solid #eef0f3; padding: 5px 8px; font-size: 11px; color: #1e3046;
    white-space: nowrap; vertical-align: middle;
  }
  .ptable tbody td.left { text-align: left; }
  .ptable tbody td.right { text-align: right; font-variant-numeric: tabular-nums; }
  .ptable tbody td.center { text-align: center; }
  // Cebra + hover, como el resto de tablas.
  .ptable tbody tr:nth-child(even) td { background: #f9fafb; }
  .prow { cursor: pointer; }
  .prow:hover td { background: #eef2f7; }
  .prow.current td { background: #eaf1fb; }
  .prow.current:hover td { background: #e0ebf9; }
  .prow:focus { outline: none; }

  // Columna de proyecto: chip de grupo + código (texto coloreado, sin badge) + nombre + chip "Actual".
  // Ancho holgado y alineado para que no se aprieten ni se solapen con las fuentes activas.
  .proj { min-width: 210px; }
  .proj > * { vertical-align: middle; }
  .p-code {
    font-family: ui-monospace, monospace; font-size: 10px; font-weight: 700;
    margin: 0 6px; vertical-align: middle;
  }
  .p-name { font-weight: 600; display: inline-block; max-width: 96px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; vertical-align: middle; }
  .p-actual {
    display: inline-block; margin-left: 6px; background: #e7effb; color: #2b5cad; border: 1px solid #c3d5f2;
    font-size: 8px; font-weight: 700; text-transform: uppercase; letter-spacing: .03em; padding: 1px 4px; border-radius: 3px;
  }
  .ptrunc { max-width: 150px; overflow: hidden; text-overflow: ellipsis; }
  .dash { color: #c8cfd8; }
  .empty { text-align: center; color: #9aa4b2; padding: 18px; font-size: 11px; }
</style>
